import React from 'react';
import {
  Box,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import { CheckCircle, Clock, FilePdf, FileX, XCircle } from 'phosphor-react';
import type { ICompany } from '../../../../hooks/useCompanies';
import PdfViewerModal from '../../../../components/viewPdfModal';
import {
  IConfigRegime,
  IImpostoTela,
  TipoValor,
  formatarEmReais,
  guiaAusenteOk,
  isAgenteNaoSeAplica,
  isAguardandoGuia,
  isCalculoNeutro,
  isEmpresaFilial,
  isPreviewTrimestral,
  isValueConsistent,
  linhasXml,
  motivosDivergencia,
  notasXmlResumo,
  possiveisMotivos,
  retencaoDoImposto,
  textoPreviaTrimestral,
} from './regras';

const VERDE = '#10B981';
const VERMELHO = '#F43F5E';

type Estado = 'ok' | 'erro' | 'neutro' | 'previa';

const FUNDO_DICA = '#1E293B';

/**
 * Balão de explicação no hover. Tooltip não serve: com fórmula + notas XML +
 * possíveis motivos ele passava da altura da tela e o navegador cortava.
 * Aqui o balão escolhe o lado com espaço, fica dentro da tela e rola por
 * dentro (dá para levar o mouse até ele) — sem esconder informação.
 */
export const Dica = ({
  conteudo,
  largura = '380px',
  children,
}: {
  conteudo: React.ReactNode;
  largura?: string;
  children: React.ReactElement;
}) => (
  <Popover trigger="hover" placement="auto" openDelay={150} closeDelay={150} isLazy>
    <PopoverTrigger>{children}</PopoverTrigger>
    <Portal>
      <PopoverContent
        w={`min(${largura}, calc(100vw - 32px))`}
        bg={FUNDO_DICA}
        color="white"
        border="none"
        borderRadius="8px"
        boxShadow="0 10px 30px rgba(15,23,42,0.35)"
        _focus={{ outline: 'none' }}
        zIndex="popover"
      >
        <PopoverArrow bg={FUNDO_DICA} />
        <PopoverBody
          px={3}
          py={2}
          fontSize="xs"
          maxH="min(70vh, 560px)"
          overflowY="auto"
          sx={{ overscrollBehavior: 'contain' }}
        >
          {conteudo}
        </PopoverBody>
      </PopoverContent>
    </Portal>
  </Popover>
);

const Status = ({ estado }: { estado: Estado }) => {
  if (estado === 'neutro') {
    return <Box w="14px" h="14px" borderRadius="full" border="2px solid #CBD5E1" />;
  }
  if (estado === 'previa') return <Clock size={16} weight="bold" color="#D97706" />;
  return estado === 'ok' ? (
    <CheckCircle size={16} weight="fill" color={VERDE} />
  ) : (
    <XCircle size={16} weight="fill" color={VERMELHO} />
  );
};

const TEXTO_FONTE_FATURAMENTO = {
  dominio: 'Faturamento do Domínio (atualizado todo dia)',
  importacao: 'Faturamento da importação do mês (Domínio sem dado em algum mês)',
};

const formatarPct = (v?: number) =>
  `${((v ?? 0) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;

/** De onde vem a base: Domínio + o que os XML mudaram (notas fora do Domínio). */
const ComposicaoXml = ({ item, rotuloDominio }: { item: ICompany; rotuloDominio?: string }) => {
  const det = item.calc_manual?.detalhes;
  const xml = det?.xml;
  const linhas = linhasXml(xml);
  if (!linhas.length) return null;
  const notas = notasXmlResumo(xml);
  const mudou =
    det?.faturamento_dominio !== undefined &&
    Math.abs((det.faturamento_acumulado ?? 0) - det.faturamento_dominio) > 0.005;
  return (
    <VStack spacing={0.5} align="start" mt={1} pt={1} borderTop="1px solid rgba(255,255,255,0.15)">
      {mudou && (
        <Text color="gray.300">
          {rotuloDominio ?? 'Domínio'}: {formatarEmReais(det?.faturamento_dominio)}
        </Text>
      )}
      {linhas.map(l => (
        <Text key={l.texto} color={l.alerta ? '#FCD34D' : 'gray.300'}>
          {l.texto}
        </Text>
      ))}
      {notas.map(n => (
        <Text key={n} color="gray.400" pl={2}>
          {n}
        </Text>
      ))}
      {(xml?.notas_total ?? 0) > notas.length && (
        <Text color="gray.400" pl={2}>
          … e mais {(xml?.notas_total ?? 0) - notas.length} na Conferência Fiscal
        </Text>
      )}
    </VStack>
  );
};

const LinhaRetencao = ({ item, imposto }: { item: ICompany; imposto: string }) => {
  const ret = retencaoDoImposto(item, imposto);
  if (ret <= 0.005) return null;
  const tri = imposto === 'irpj' || imposto === 'csll';
  return (
    <Text color="#93C5FD" mt={1}>
      Retenção sofrida{tri ? ' no trimestre' : ''} (XML): {formatarEmReais(ret)} — a guia pode vir
      já sem ela ({formatarEmReais(Math.max((item.calc_manual?.[imposto] ?? 0) - ret, 0))})
    </Text>
  );
};

/** Fórmula do cálculo agente (presunção do LP; não cumulativo do LR). */
export const FormulaAgente = ({ item, imposto }: { item: ICompany; imposto: string }) => {
  const det = item.calc_manual?.detalhes;
  if (!det) return null;
  const isMensal = det.periodicidade === 'mensal';
  const periodicidade = isMensal ? 'Mensal (estimativa)' : 'Trimestral';
  const valor = formatarEmReais(item.calc_manual?.[imposto] || 0);
  const descontado = item.receitas_financeiras?.descontado ?? 0;
  const fonte = det.fonte_faturamento ? TEXTO_FONTE_FATURAMENTO[det.fonte_faturamento] : null;
  const linhaFonte = fonte && <Text color="gray.300">{fonte}</Text>;

  if (imposto === 'icms') {
    if (det.icms_dominio === undefined) return null;
    return (
      <VStack spacing={1} align="start" fontSize="xs">
        <Text fontWeight="bold">ICMS | Apuração do Domínio + XML</Text>
        <Text>Apurado no Domínio: {formatarEmReais(det.icms_dominio)}</Text>
        {Math.abs(det.icms_ajuste_xml ?? 0) > 0.005 && (
          <Text color="#FCD34D">
            {(det.icms_ajuste_xml ?? 0) > 0 ? '+' : '−'} ICMS das notas que o Domínio não reflete:{' '}
            {formatarEmReais(Math.abs(det.icms_ajuste_xml ?? 0))}
          </Text>
        )}
        <Text color="gray.300" mt={1}>
          = {valor}
        </Text>
        <Text color="gray.400">Crédito das entradas: o do Domínio.</Text>
      </VStack>
    );
  }

  if ((imposto === 'pis' || imposto === 'cofins') && det.lucro_real) {
    const aliq = imposto === 'pis' ? det.aliquota_pis : det.aliquota_cofins;
    const credito = (imposto === 'pis' ? det.credito_pis : det.credito_cofins) ?? 0;
    const debito = det.faturamento_acumulado * (aliq ?? 0);
    return (
      <VStack spacing={1} align="start" fontSize="xs">
        <Text fontWeight="bold">{imposto.toUpperCase()} | Não cumulativo</Text>
        <Text>Faturamento: {formatarEmReais(det.faturamento_acumulado)}</Text>
        {linhaFonte}
        <Text>
          Débito: {formatarEmReais(det.faturamento_acumulado)} × {formatarPct(aliq)} ={' '}
          {formatarEmReais(debito)}
        </Text>
        <Text>(−) Crédito apurado no Domínio: {formatarEmReais(credito)}</Text>
        <Text color="gray.300" mt={1}>
          = {valor}
        </Text>
        <Text color="gray.400">
          Crédito do Domínio: energia, aluguel e depreciação não têm XML de entrada.
        </Text>
        {descontado > 0 && (
          <Text color="#FCD34D" mt={1}>
            Sem a receita financeira de {formatarEmReais(descontado)}
          </Text>
        )}
        <LinhaRetencao item={item} imposto={imposto} />
        <ComposicaoXml item={item} />
      </VStack>
    );
  }

  if (imposto === 'pis' || imposto === 'cofins') {
    const aliq = imposto === 'pis' ? '0,65%' : '3%';
    return (
      <VStack spacing={1} align="start" fontSize="xs">
        <Text fontWeight="bold">{imposto.toUpperCase()} | Regime Cumulativo</Text>
        <Text>Faturamento: {formatarEmReais(det.faturamento_acumulado)}</Text>
        {linhaFonte}
        <Text>Alíquota: {aliq}</Text>
        <Text color="gray.300" mt={1}>
          {formatarEmReais(det.faturamento_acumulado)} × {aliq}
        </Text>
        {descontado > 0 && (
          <Text color="#FCD34D" mt={1}>
            Sem a receita financeira de {formatarEmReais(descontado)}
          </Text>
        )}
        <LinhaRetencao item={item} imposto={imposto} />
        <ComposicaoXml item={item} />
      </VStack>
    );
  }

  if (imposto !== 'irpj' && imposto !== 'csll') return null;
  const irpj = imposto === 'irpj';
  const presSaida = irpj ? '8%' : '12%';
  const aliq = irpj ? 0.15 : 0.09;
  const base = irpj ? det.base_irpj : det.base_csll;
  const temAdicional = irpj && det.irpj_adicional > 0;
  const nome = irpj ? 'IRPJ' : 'CSLL';
  const pct = irpj ? '15%' : '9%';
  const receitaFinanceira = det.receita_financeira_tri ?? 0;
  // O que a base tem além da presunção e da receita financeira é a
  // majoração da LC 224 (10% sobre o excedente acumulado no ano)
  const presumido = det.vl_saida_tri * (irpj ? 0.08 : 0.12) + det.vl_servico_tri * 0.32;
  const majoracao = base - presumido - receitaFinanceira;

  return (
    <VStack spacing={1} align="start" fontSize="xs">
      <Text fontWeight="bold">
        {nome} | {periodicidade}
      </Text>
      {linhaFonte}
      {det.vl_saida_tri > 0 && (
        <Text>
          Comércio: {formatarEmReais(det.vl_saida_tri)} × {presSaida}
        </Text>
      )}
      {det.vl_servico_tri > 0 && <Text>Serviço: {formatarEmReais(det.vl_servico_tri)} × 32%</Text>}
      {majoracao > 0.01 && <Text>Majoração LC 224: + {formatarEmReais(majoracao)}</Text>}
      {receitaFinanceira > 0 && (
        <Text>Receita financeira: {formatarEmReais(receitaFinanceira)} × 100%</Text>
      )}
      <Text>
        Base {nome}
        {isMensal ? ' (acum.)' : ''}: {formatarEmReais(base)}
      </Text>
      <Text>
        {isMensal ? `${nome} acum.` : `Alíq. ${pct}`}: {formatarEmReais(base)} × {pct} ={' '}
        {formatarEmReais(base * aliq)}
      </Text>
      {temAdicional && (
        <Text>
          Adic. 10%: ({formatarEmReais(det.base_irpj)} − R$ 60.000) × 10% ={' '}
          {formatarEmReais(det.irpj_adicional)}
        </Text>
      )}
      {isMensal ? (
        <>
          <Text fontWeight="bold" mt={1}>
            Total acum.: {formatarEmReais(irpj ? det.irpj_acumulado : det.csll_acumulado)}
          </Text>
          <Text>
            (-) Meses anteriores: {formatarEmReais(irpj ? det.irpj_anterior : det.csll_anterior)}
          </Text>
          <Text color="gray.300" mt={1}>
            = {valor}
          </Text>
        </>
      ) : (
        <Text color="gray.300" mt={1}>
          {formatarEmReais(base * aliq)}
          {temAdicional ? ` + ${formatarEmReais(det.irpj_adicional)}` : ''} = {valor}
        </Text>
      )}
      <LinhaRetencao item={item} imposto={imposto} />
      <ComposicaoXml item={item} rotuloDominio="Faturamento do mês no Domínio" />
    </VStack>
  );
};

/** Card só de valor (sem guia/cálculo): retenções sofridas pelos XML. */
const ImpostoInfoCard = ({ item, imposto }: { item: ICompany; imposto: IImpostoTela }) => {
  const det = item.calc_manual?.detalhes;
  const valor = item.calc_manual?.[imposto.key as 'retencoes_sofridas'] ?? 0;
  const ret = det?.retencoes_sofridas || {};
  const partes = (['pis', 'cofins', 'csll', 'irrf', 'inss'] as const).filter(
    k => (ret[k] ?? 0) > 0.005,
  );
  const dica = (
    <VStack spacing={1} align="start" fontSize="xs">
      <Text>{imposto.dica}</Text>
      {partes.map(k => (
        <Text key={k}>
          {k.toUpperCase()}: {formatarEmReais(ret[k])}
        </Text>
      ))}
      {partes.length > 0 && (
        <Text color="gray.400">Detalhe por imposto: matriz + filiais no mês.</Text>
      )}
    </VStack>
  );
  return (
    <Dica conteudo={dica} largura="340px">
      <Box
        w="full"
        h="full"
        p="12px 14px"
        borderRadius="16px"
        border="1px solid rgba(15,23,42,0.07)"
        bg="rgba(255,255,255,0.7)"
        cursor="help"
      >
        <Text fontSize="12.5px" fontWeight={800} color="#0F172A" letterSpacing="0.02em" mb={2}>
          {imposto.rotulo}
        </Text>
        <Text fontSize="11px" color="#94A3B8">
          XML (tomador reteve)
        </Text>
        <Text
          fontSize="14px"
          fontWeight={700}
          color={valor > 0 ? '#1D4ED8' : '#94A3B8'}
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {formatarEmReais(valor)}
        </Text>
      </Box>
    </Dica>
  );
};

const LinhaValor = ({
  rotulo,
  estado,
  texto,
  dica,
  sublinhado,
}: {
  rotulo: string;
  estado: Estado;
  texto: string;
  dica?: React.ReactNode;
  sublinhado?: boolean;
}) => {
  const valor = (
    <Text
      fontSize="12px"
      fontWeight={600}
      color={estado === 'erro' ? '#BE123C' : '#334155'}
      textDecoration={sublinhado ? 'underline dotted' : undefined}
      cursor={dica ? 'help' : undefined}
      flex="none"
      textAlign="right"
      whiteSpace="nowrap"
      sx={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {texto}
    </Text>
  );
  return (
    <Flex align="center" gap={2} minW={0}>
      <Flex w="16px" justify="center" flex="none">
        <Status estado={estado} />
      </Flex>
      <Text fontSize="11px" color="#94A3B8" flex="none" whiteSpace="nowrap">
        {rotulo}
      </Text>
      <Box flex={1} />
      {dica ? <Dica conteudo={dica}>{valor}</Dica> : valor}
    </Flex>
  );
};

/** Um card de imposto: guia × cálculo do Domínio × cálculo agente. */
export const ImpostoCard = ({
  item,
  imposto,
  cfg,
}: {
  item: ICompany;
  imposto: IImpostoTela;
  cfg: IConfigRegime;
}) => {
  if (imposto.informativo) return <ImpostoInfoCard item={item} imposto={imposto} />;
  return <ImpostoComparado item={item} imposto={imposto} cfg={cfg} />;
};

const ImpostoComparado = ({
  item,
  imposto,
  cfg,
}: {
  item: ICompany;
  imposto: IImpostoTela;
  cfg: IConfigRegime;
}) => {
  const { key } = imposto;
  const filial = imposto.soMatriz && isEmpresaFilial(item.cnpj);
  const pdf = imposto.temGuiaPdf && !filial ? item.impostos_guia?.[`${key}_pdf`] : null;
  // Guia com PDF gravado que o backend não achou no S3 (arquivo movido ou
  // renomeado): avisa em vez de abrir o link com 404
  const pdfNaoEncontrado =
    !pdf && !filial && !!item.impostos_guia?.pdf_nao_encontrado?.includes(key);
  const ok = (tipo: TipoValor): Estado => (isValueConsistent(item, key, tipo, cfg) ? 'ok' : 'erro');

  // Igual às telas antigas: só `null` é "não encontrada".
  const guiaNula = item.impostos_guia?.[key] === null;
  const calcValor = item.impostos_calculo?.[key];
  const agenteValor = item.calc_manual?.[key];
  const previa = isPreviewTrimestral(item, key, cfg);
  const naoSeAplica = isAgenteNaoSeAplica(key, cfg);
  const formula = cfg.formulaAgente ? <FormulaAgente item={item} imposto={key} /> : null;
  const temFormula =
    !!formula &&
    !!item.calc_manual?.detalhes &&
    (['pis', 'cofins', 'irpj', 'csll'].includes(key) ||
      (key === 'icms' && item.calc_manual.detalhes.icms_dominio !== undefined));

  let estadoGuia: Estado = ok('guia');
  if (guiaNula) estadoGuia = guiaAusenteOk(item, key, cfg) ? 'ok' : 'erro';
  // IRPJ/CSLL devido sem guia: a guia ainda não saiu (pendência, não erro)
  const aguardandoGuia = !filial && isAguardandoGuia(item, key, cfg);
  if (aguardandoGuia) estadoGuia = 'previa';
  let textoGuia = guiaNula ? 'Não encontrada' : formatarEmReais(item.impostos_guia?.[key]);
  if (aguardandoGuia) textoGuia = 'Aguardando guia';
  const estadoCalculo: Estado = isCalculoNeutro(item, key, cfg) ? 'neutro' : ok('calculo');
  let estadoAgente: Estado = previa ? 'previa' : ok('manual');
  if (naoSeAplica) estadoAgente = 'neutro';
  const algumErro = !filial && [estadoGuia, estadoCalculo, estadoAgente].includes('erro');

  const vazio = (v: unknown) => v === null || v === undefined;
  let textoAgente = formatarEmReais(agenteValor);
  if (vazio(agenteValor)) textoAgente = previa ? 'R$ 0,00' : 'Não encontrado';
  if (naoSeAplica) textoAgente = 'Não se aplica';
  let dicaAgente: React.ReactNode;
  if (naoSeAplica) {
    dicaAgente = <Text fontSize="xs">{cfg.dicaAgenteNaoSeAplica}</Text>;
  } else if (previa) {
    dicaAgente = (
      <VStack align="start" spacing={2}>
        <Text fontSize="xs">{textoPreviaTrimestral(item)}</Text>
        {temFormula && formula}
      </VStack>
    );
  } else if (temFormula) {
    dicaAgente = formula;
  }
  // Com erro, o tooltip do agente diz o motivo provável (quando o backend
  // identificou) e os possíveis motivos para conferir (sempre)
  const motivos =
    algumErro && !naoSeAplica ? motivosDivergencia(item).filter(m => m.impostos.includes(key)) : [];
  const possiveis = algumErro ? possiveisMotivos(item, key, cfg) : [];
  if (motivos.length || possiveis.length) {
    dicaAgente = (
      <VStack align="start" spacing={2}>
        {dicaAgente}
        {motivos.map(m => (
          <Text key={m.chave} fontSize="xs" color="#FCD34D">
            Provável motivo: {m.titulo}
          </Text>
        ))}
        {possiveis.length > 0 && (
          <VStack
            align="start"
            spacing={1}
            pt={dicaAgente || motivos.length ? 1 : 0}
            borderTop={
              dicaAgente || motivos.length ? '1px solid rgba(255,255,255,0.15)' : undefined
            }
          >
            <Text fontSize="xs" fontWeight="bold">
              Não bateu — possíveis motivos para conferir:
            </Text>
            {possiveis.map(p => (
              <Text key={p} fontSize="xs" color="gray.200">
                • {p}
              </Text>
            ))}
          </VStack>
        )}
      </VStack>
    );
  }

  let titulo = imposto.dica ?? (filial ? 'Empresa filial - sem guia' : 'Guia não disponível');
  if (pdfNaoEncontrado)
    titulo = 'PDF da guia não encontrado no S3 (arquivo movido ou renomeado na pasta da empresa)';
  if (pdf) titulo = 'Clique para visualizar a guia';

  const corpo = (
    <Box
      // Com guia vira botão (o PdfViewerModal injeta o onClick)
      {...(pdf ? { as: 'button', type: 'button' } : {})}
      textAlign="left"
      w="full"
      h="full"
      p="12px 14px"
      borderRadius="16px"
      border="1px solid"
      borderColor={algumErro ? 'rgba(244,63,94,0.28)' : 'rgba(15,23,42,0.07)'}
      bg={algumErro ? 'rgba(255,241,242,0.65)' : 'rgba(255,255,255,0.7)'}
      cursor={pdf ? 'pointer' : 'default'}
      transition="border-color 0.15s, box-shadow 0.15s"
      _hover={
        pdf
          ? {
              borderColor: 'rgba(27,136,245,0.45)',
              boxShadow: '0 6px 18px -8px rgba(27,136,245,0.35)',
            }
          : undefined
      }
      title={titulo}
    >
      <Flex align="center" justify="space-between" mb={2} gap={2}>
        <Text fontSize="12.5px" fontWeight={800} color="#0F172A" letterSpacing="0.02em">
          {imposto.rotulo}
          {imposto.mostraPercentual && !filial && (
            <Text as="span" fontWeight={500} color="#94A3B8">
              {' '}
              · {item.impostos_calculo?.[`${key}_prct`] ?? 0}%
            </Text>
          )}
        </Text>
        {pdf && <FilePdf size={15} weight="duotone" color="#1B88F5" />}
        {pdfNaoEncontrado && <FileX size={15} weight="duotone" color="#94A3B8" />}
      </Flex>
      {filial ? (
        <Text fontSize="12px" fontWeight={600} color="#94A3B8" py={3}>
          Empresa filial
        </Text>
      ) : (
        <VStack align="stretch" spacing="6px">
          <LinhaValor
            rotulo="Guia"
            estado={estadoGuia}
            texto={textoGuia}
            dica={
              aguardandoGuia ? (
                <Text fontSize="xs">
                  {imposto.rotulo} devido e a guia ainda não saiu: fica no KPI &quot;Sem guia
                  IRPJ/CSLL&quot;, não conta como erro.
                </Text>
              ) : undefined
            }
          />
          <LinhaValor
            rotulo="Cálculo"
            estado={estadoCalculo}
            texto={vazio(calcValor) ? 'Não encontrado' : formatarEmReais(calcValor)}
          />
          <LinhaValor
            rotulo="Agente"
            estado={estadoAgente}
            texto={textoAgente}
            dica={dicaAgente}
            sublinhado={!!dicaAgente}
          />
        </VStack>
      )}
    </Box>
  );

  return pdf ? <PdfViewerModal pdfUrl={pdf}>{corpo}</PdfViewerModal> : corpo;
};
